import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query'
import { useState, useCallback, useEffect, useMemo } from 'react'
import type { User } from '@opallabs/concepts'
import {
  type OpalApiRequestParams,
  type OpalApiResponseDocument,
} from '../../../schema'
import { findResourceOfType } from '../../../schema/validation/resourceObject'
import { users } from '../../../v3/users'
import { globalQueryKeys } from '../../queryKeys'

type ReadUsersInclude = OpalApiRequestParams<'ReadUsersV3'>['query']['include']
type ReadUsersFilter = OpalApiRequestParams<'ReadUsersV3'>['query']['filter']
type ResponseDocument = OpalApiResponseDocument<'ReadUsersV3', 200>

type UseUsersByIDParams = {
  userIds: Array<string>
  filter?: ReadUsersFilter
  include?: ReadUsersInclude
  enabled?: boolean
  /** Defaults to the app's setting. Pass `false` where a failed read must not replace the page. */
  useErrorBoundary?: boolean
}

const defaultInclude = [
  'profile',
  'profile.avatar_asset_reference',
] as const satisfies ReadUsersInclude

const USERS_CHUNK_SIZE = 100

const selectUsers = (data?: InfiniteData<ResponseDocument>): User[] => {
  if (!data) return []

  return data.pages.reduce<User[]>((allUsers, page) => {
    const pageUsers = page.data.map<User>((user) => {
      const profile = page.included?.find(
        findResourceOfType(
          'user_profile',
          'V3',
          user.relationships.profile.data?.id
        )
      )
      const asset = profile
        ? page.included?.find(
            findResourceOfType(
              'asset_reference',
              'V3',
              profile.relationships.avatar_asset_reference.data?.id
            )
          )
        : undefined

      return {
        type: 'user',
        uuid: user.id,
        legacyId: user.attributes.legacy_id,
        familyName: user.attributes.last_name,
        givenName: user.attributes.first_name,
        name: user.attributes.full_name,
        activeWorkspaceMember: user.attributes.is_active || false,
        asset: asset
          ? {
              id: asset.id,
              url: asset.attributes.full_url || '',
              title: asset.attributes.filename || '',
            }
          : undefined,
      }
    })

    return [...allUsers, ...pageUsers]
  }, [])
}

export function useUsersByID({
  userIds,
  filter,
  include = defaultInclude,
  enabled,
  useErrorBoundary,
}: UseUsersByIDParams) {
  const [initiatedAllRequests, setInitiatedAllRequests] = useState(false)

  const shouldFetchNextPage = useCallback(
    (data: InfiniteData<ResponseDocument>, totalChunks: number) => {
      const currentChunk = data.pages.length

      if (currentChunk < totalChunks) {
        return true
      }

      setInitiatedAllRequests(true)
      return false
    },
    []
  )

  const totalChunks = Math.ceil(userIds.length / USERS_CHUNK_SIZE)

  const {
    data,
    isFetchingNextPage,
    isFetching,
    isInitialLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: globalQueryKeys.usersById(userIds, filter, include),
    queryFn: ({ pageParam = 0 }) => {
      const start = pageParam * USERS_CHUNK_SIZE
      const end = start + USERS_CHUNK_SIZE
      const chunkIds = userIds.slice(start, end)

      return users.readCollection({
        query: {
          filter: {
            ...filter,
            id: chunkIds,
          },
          include,
          page: {
            limit: 200,
          },
        },
      })
    },
    getNextPageParam: (_lastPage, pages) => pages.length,
    enabled,
    // An explicit `undefined` would replace the app's default
    ...(useErrorBoundary !== undefined && { useErrorBoundary }),
  })

  const resultUsers = useMemo(() => selectUsers(data), [data])

  useEffect(() => {
    if (!data) return
    if (
      !initiatedAllRequests &&
      !isFetching &&
      !isFetchingNextPage &&
      shouldFetchNextPage(data, totalChunks)
    ) {
      fetchNextPage()
    }
  }, [
    initiatedAllRequests,
    isFetchingNextPage,
    isFetching,
    data,
    totalChunks,
    shouldFetchNextPage,
    fetchNextPage,
  ])

  return {
    data: resultUsers,
    isFetching: isFetching || isFetchingNextPage,
    isInitialLoading,
    fetchingComplete: initiatedAllRequests && !isFetching,
  }
}
